<template>
	<section class="loader-container">
		<div class="loader">
			<div
				:class="['first-circle', { animate: firstCircleAnimate }]"
				:style="{ background: firstCircleBackground }"
			></div>
			<div
				:class="['firstmid-circle', { animate: firstMidCircleAnimate }]"
			></div>

			<div
				class="position-relative"
				:class="['second-circle', { animate: secondRectangleAnimate }]"
				:style="{ background: secondBgColor }"
			>
				<div class="white-line"></div>
				<div class="white-round"></div>
			</div>
		</div>

		<div v-if="statusText || statusSubtext" class="status-text-wrapper">
			<div class="status-text-container">
				<transition-group name="text-transition" tag="div">
					<div
						v-for="text in [{ text: statusText, id: statusTextKey }]"
						:key="text.id"
						class="status-text-main"
					>
						{{ text.text }}
					</div>
				</transition-group>
			</div>

			<div v-if="statusSubtext" class="status-subtext-container">
				<transition-group name="text-transition" tag="div">
					<div
						v-for="text in [{ text: statusSubtext, id: statusSubtextKey }]"
						:key="text.id"
						class="status-text-sub"
					>
						{{ text.text }}
					</div>
				</transition-group>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: "SpinnerComponent",
	props: {
		statusText: {
			type: String,
			default: "",
		},
		statusSubtext: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			firstCircleAnimate: false,
			firstMidCircleAnimate: false,
			midContentAnimate: false,
			secondRectangleAnimate: false,
			firstCircleBackground: "#f0f6ff",
			secondBgColor: "#ebedf0",
			timeouts: [],
			intervals: null,
			statusTextKey: 0,
			statusSubtextKey: 0,
			previousStatusText: "",
			previousStatusSubtext: "",
		};
	},
	watch: {
		statusText(newValue, oldValue) {
			if (newValue !== oldValue && oldValue !== "") {
				this.statusTextKey++;
			}
			this.previousStatusText = newValue;
		},
		statusSubtext(newValue, oldValue) {
			if (newValue !== oldValue && oldValue !== "") {
				this.statusSubtextKey++;
			}
			this.previousStatusSubtext = newValue;
		},
	},
	mounted() {
		document.addEventListener("visibilitychange", this.handleVisibilityChange);

		this.restartAnimationCycle();
		this.intervals = setInterval(() => {
			this.restartAnimationCycle();
		}, 1500);
	},
	methods: {
		handleVisibilityChange() {
			if (document.hidden) {
				this.timeouts.forEach((timeout) => {
					clearTimeout(timeout);
				});
				clearInterval(this.intervals);
			} else {
				this.restartAnimationCycle();
				this.intervals = setInterval(() => {
					this.restartAnimationCycle();
				}, 1500);
			}
		},
		async triggerAnimationCycle() {
			this.timeouts.push(
				setTimeout(() => {
					this.firstCircleAnimate = true;
					this.firstCircleBackground = "#f0f6ff";
				}, 300)
			);
			this.timeouts.push(
				setTimeout(() => {
					this.firstCircleBackground = "unset";
					this.firstMidCircleAnimate = true;
				}, 700)
			);
			this.timeouts.push(
				setTimeout(() => {
					this.firstCircleBackground = "unset";
					this.secondRectangleAnimate = true;
					this.secondBgColor = "#ebedf0";
				}, 1100)
			);
			this.firstCircleBackground = "#f0f6ff";
		},
		async restartAnimationCycle() {
			this.timeouts = [];
			this.firstCircleAnimate = false;
			this.firstMidCircleAnimate = false;
			this.secondRectangleAnimate = false;

			this.triggerAnimationCycle();
		},
	},
	beforeDestroy() {
		// Clean up intervals and timeouts when component is destroyed
		this.timeouts.forEach((timeout) => {
			clearTimeout(timeout);
		});
		clearInterval(this.intervals);
		document.removeEventListener(
			"visibilitychange",
			this.handleVisibilityChange
		);
	},
};
</script>

<style scoped>
.loader-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100vw;
	height: 100vh;
}

.loader {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-left: -170px;
}

.status-text-wrapper {
	position: relative;
	text-align: center;
	min-width: 300px;
	padding-left: 8px;
	padding-right: 8px;
	font-size: 13px;
	color: #333;
	margin-top: 20px;
	margin-left: -120px;
}

.status-text-container,
.status-subtext-container {
	position: relative;
	min-height: 20px;
}

.status-text-main,
.status-text-sub {
	font-family: "manrope", sans-serif;
	font-weight: 700;
	color: #fcfcfc;
	font-size: 18px;
	position: absolute;
	left: 0;
	right: 0;
}

.status-text-main {
	font-weight: 500;
	font-size: 15px;
	margin-bottom: 4px;
}

.status-text-sub {
	font-weight: 400;
	font-size: 13px;
	opacity: 0.7;
}

/* Transition for text animations */
.text-transition-enter-active,
.text-transition-leave-active {
	transition: all 0.4s ease;
}

.text-transition-enter {
	opacity: 0;
	transform: translateY(16px) scale(0.96);
}

.text-transition-leave-to {
	opacity: 0;
	transform: translateY(-16px) scale(0.96);
}

.first-circle {
	width: 45px;
	height: 45px;
	position: relative;
	border-top-left-radius: 50px;
	border-bottom-left-radius: 50px;
	transform: rotate(49deg);
	z-index: 8999;
	background: #f0f6ff;
	overflow: hidden;
}

.first-circle::before {
	position: absolute;
	content: "";
	bottom: 0px;
	left: 0px;
	width: 0;
	height: 100%;
	background-color: #68a5ff;
	transition: width 0.4s linear;
	z-index: -1;
}

.first-circle.animate::before {
	width: 100%;
	animation: growWidth 0.4s linear forwards;
}

@keyframes growWidth {
	from {
		width: 0%;
	}
	to {
		width: 100%;
	}
}
.firstmid-circle {
	height: 16px;
	width: 18px;
	border-bottom-right-radius: 100%;
	border-top-right-radius: 0px;
	z-index: 500;
	transform: rotate(48deg);
	margin-top: 27px;
	background: #f0f6ff;
	overflow: hidden;
}

.firstmid-circle::before {
	position: absolute;
	content: "";
	bottom: 0px;
	left: 0px;
	width: 0;
	height: 100%;
	background-color: #68a5ff;
	transition: width 0.2s linear;
	z-index: -1;
}

.firstmid-circle.animate::before {
	width: 100%;
	animation: growMidWidth 0.2s linear forwards;
}

@keyframes growMidWidth {
	from {
		width: 0%;
	}
	to {
		width: 100%;
	}
}

.second-circle {
	width: 41px;
	height: 180px;
	transform: rotate(49deg);
	margin-left: 18px;
	border-top-left-radius: 50px;
	border-top-right-radius: 50px;
	border-bottom-right-radius: 22px;
	position: relative;
	margin-top: -27px;
	background: #ebedf0;
}

.second-circle::before {
	position: absolute;
	content: "";
	bottom: 0px;
	left: 0px;
	width: 100%;
	height: 0;
	background-color: #344966;
	transition: height 0.3s linear;
	z-index: -1;
	border-top-left-radius: 50px;
	border-top-right-radius: 50px;
	border-bottom-right-radius: 22px;
}

.second-circle.animate::before {
	height: 100%;
	animation: growsecondWidth 0.3s linear forwards;
}

@keyframes growsecondWidth {
	from {
		height: 0%;
	}
	to {
		height: 100%;
	}
}

.white-round {
	position: absolute;
	bottom: 26px;
	left: 0px;
	height: 21px;
	width: 21px;
	border-radius: 0px 246px 0 0;
	background-color: #fff;
	transform: rotate(89deg);
}

.white-line {
	position: absolute;
	bottom: 0px;
	height: 33px;
	width: 4px;
	background: #fff;
}
</style>
